import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHourglassHalf } from "react-icons/fa6";
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Badge,
  Icon,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Code,
  Divider,
  Link,
  Tooltip,
  Wrap,
  WrapItem,
  Flex,
  Table,
  Tbody,
  Tr,
  Td,
  Image
} from '@chakra-ui/react';
import { 
  FaServer,
  FaCloud,
  FaPlay,
  FaDownload,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock,
  FaChartLine,
  FaMicrochip,
  FaSignOutAlt,
  FaTimes,
  FaListOl,
  FaSyncAlt,
  FaCreditCard,
  FaCrown,
  FaCalendarAlt,
  FaInfoCircle,
  FaCog,
  FaBolt,
  FaShieldAlt,
  FaBrain,
  FaBan,
  FaCoins,
  FaShoppingCart,
  FaBookOpen,
  FaTrash,
  FaRocket
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { getProductDisplayName } from '../utils/productNames';
import Footer from './Footer';
import NavDropdowns from './NavDropdowns';

// ------------------ Job Card ------------------
const JobCard = ({ job, handleDownload, handleViewError, downloadingJobs }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const isDownloading = downloadingJobs.includes(job.job_id);
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'green';
      case 'running':
        return 'blue';
      case 'preprocessing':
        return 'purple';
      case 'enqueue':
        return 'yellow';
      case 'failed':
        return 'red';
      case 'cancelled':
        return 'gray';
      case 'deleted':
        return 'red';
      default:
        return 'gray';
    }
  };
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return FaCheckCircle;
      case 'running':
        return FaClock;
      case 'preprocessing':
        return FaCog;
      case 'enqueue':
        return FaHourglassHalf;
      case 'failed':
        return FaExclamationTriangle;
      case 'cancelled':
        return FaBan;
      case 'deleted':
        return FaTrash;
      default:
        return FaClock;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m`;
  };

  const renderActionButton = () => {
    const status = job.status?.toLowerCase();
    
    if (status === 'failed') {
      return (
        <Button 
          leftIcon={<FaExclamationTriangle />} 
          colorScheme="red" 
          size="sm" 
          width="full"
          onClick={() => handleViewError(job)}
        >
          View Error
        </Button>
      );
    }
    
    if (status === 'completed') {
      return (
        <Button
          leftIcon={isDownloading ? <Spinner size="xs" /> : <FaDownload />}
          colorScheme="brand" 
          size="sm" 
          width="full"

          onClick={() => handleDownload(job.job_id)}
          isLoading={isDownloading}
          loadingText="Getting URL..."
        >
          Download Results
        </Button>
      );
    }
    if (status === 'cancelled') {
      return (
        <Button
          leftIcon={<FaBan />}
          colorScheme="gray" 
          size="sm" 
          width="full"
          isDisabled={true}
        >
          Cancelled
        </Button>
      );
    }
    if (status === 'deleted') {
      return (
        <Button
          leftIcon={<FaTrash />}
          colorScheme="red" 
          size="sm" 
          width="full"
          isDisabled={true}
        >
          Deleted
        </Button>
      );
    }
    
    return (
      <Button 
      leftIcon={<FaHourglassHalf />} 
      colorScheme="gray" 
      size="sm" 
      width="full"
      isDisabled={true}
    >
      {status === 'running' ? 'Running...' : 'Results Pending'}
    </Button>
);
  };

  return (
    <Card bg={cardBg} border="1px" borderColor={borderColor} shadow="md">
      <CardHeader pb={2}>
        <VStack spacing={2} align="stretch">
          <Heading size="md" noOfLines={1}>
            {job.job_name || `Job ${job.job_id}`}
          </Heading>
          <HStack justify="space-between">
            <Badge 
              colorScheme={getStatusColor(job.status)} 
              px={2}
              py={1}
              fontSize="sm"
            >
              <HStack spacing={1}>
                <Icon as={getStatusIcon(job.status)} boxSize={3} />
                <Text>{job.status?.toUpperCase() || 'UNKNOWN'}</Text>
              </HStack>
            </Badge>
            <Text fontSize="xs" color="gray.500">
              {formatDate(job.enqueued_at)}
            </Text>
          </HStack>
        </VStack>
      </CardHeader>
      <CardBody pt={2} pb={4}>
        <Text color="gray.600" fontSize="sm" noOfLines={3} mb={3}>
          {job.job_desc || 'No description provided'}
        </Text>
        <HStack spacing={4} fontSize="sm" color="gray.500">
          <Text>Tool: {job.tool_name}</Text>
          <Text>Duration: {formatDuration(job.total_duration)}</Text>
        </HStack>
      </CardBody>
      <CardFooter pt={0}>
        {renderActionButton()}
      </CardFooter>
    </Card>
  );
};

// ------------------ Dashboard ------------------
const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [jobStats, setJobStats] = useState(null);
  const [userJobs, setUserJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobsError, setJobsError] = useState(null);
  const [downloadingJobs, setDownloadingJobs] = useState([]);
  
  // Job eligibility states
  const [jobEligibility, setJobEligibility] = useState(null);
  const [jobEligibilityLoading, setJobEligibilityLoading] = useState(true);
  


  // Subscription states
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);

  // Purchase summary states
  const [purchaseSummary, setPurchaseSummary] = useState(null);
  const [purchaseSummaryLoading, setPurchaseSummaryLoading] = useState(true);
  const [purchaseSummaryError, setPurchaseSummaryError] = useState(null);
  
  // Modal state for error dialog
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedError, setSelectedError] = useState({ jobId: '', error: '', jobName: '' });

  // GPU Status modal state
  const { isOpen: isGpuModalOpen, onOpen: onGpuModalOpen, onClose: onGpuModalClose } = useDisclosure();
  const [queueStats, setQueueStats] = useState(null);
  const [queueStatsLoading, setQueueStatsLoading] = useState(false);
  const [queueStatsError, setQueueStatsError] = useState(null);

  const handleAuthError = useAuthErrorHandler();

  useEffect(() => {
    fetchJobStats();
    fetchUserJobs();
    fetchSubscriptionDetails();
    fetchJobEligibility();
    fetchPurchaseSummary();
  }, []);

  const fetchJobStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const stats = await apiService.getUserJobStats();
      setJobStats(stats);
    } catch (err) {
      // Check if it's an auth error first
      if (!handleAuthError(err)) {
        console.error('Failed to fetch job stats:', err);
        setError('Failed to load job statistics');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserJobs = async () => {
    try {
      setJobsLoading(true);
      setJobsError(null);
      const jobs = await apiService.getUserJobs();
      // Sort by enqueued_at date (newest first) and take last 4
      const sortedJobs = jobs
        .sort((a, b) => new Date(b.enqueued_at || 0) - new Date(a.enqueued_at || 0))
        .slice(0, 4);
      setUserJobs(sortedJobs);
    } catch (err) {
      // Check if it's an auth error first
      if (!handleAuthError(err)) {
        console.error('Failed to fetch user jobs:', err);
        setJobsError('Failed to load jobs');
      }
    } finally {
      setJobsLoading(false);
    }
  };


  // Add the fetchJobEligibility function
  const fetchJobEligibility = async () => {
    try {
      setJobEligibilityLoading(true);
      const eligibility = await apiService.checkJobEligibility();
      
      // Handle the new API response format
      if (eligibility && typeof eligibility === 'object') {
        setJobEligibility({
          is_eligible: eligibility.is_eligible || false,
          remaining_hours: eligibility.remaining_hours || 0,
          reason: eligibility.reason || 'Unknown eligibility status'
        });
      } else {
        // Fallback for unexpected response format
        setJobEligibility({
          is_eligible: false,
          remaining_hours: 0,
          reason: 'Invalid response format'
        });
      }
    } catch (err) {
      // Check if it's an auth error first
      if (!handleAuthError(err)) {
        console.error('Failed to fetch job eligibility:', err);
        setJobsError('Failed to fetch job eligibility');
        // Default to false if we can't check eligibility
        setJobEligibility({
          is_eligible: false,
          remaining_hours: 0,
          reason: 'Failed to check eligibility'
        });
      }
    } finally {
      setJobEligibilityLoading(false);
    }
  };
  
    
    
  const handleDownload = async (jobId) => {
    try {
      setDownloadingJobs((prev) => [...prev, jobId]);
  
      // Step 1: ask backend for signed URL
      const response = await fetch(`${apiService.baseURL}/get-download-link/${jobId}`, {
        headers: apiService.getHeaders(true), // include auth
      });
      if (!response.ok) throw new Error("Failed to get download link");
      const { download_url } = await response.json();
  
      // Step 2: redirect browser to signed URL (streams large file)
      window.open(`${apiService.baseURL}${download_url}`, "_blank");
    } catch (error) {
      console.error("Download failed:", error);
      alert(`Download failed: ${error.message}`);
    } finally {
      setDownloadingJobs((prev) => prev.filter((id) => id !== jobId));
    }
  };

  const handleViewError = (job) => {
    setSelectedError({
      jobId: job.job_id,
      error: job.error || 'No error details available',
      jobName: job.job_name || `Job ${job.job_id}`
    });
    onOpen();
  };

  const handleViewGpuStatus = async () => {
    try {
      setQueueStatsLoading(true);
      setQueueStatsError(null);
      const stats = await apiService.getUserQueueStats();
      setQueueStats(stats);
      onGpuModalOpen();
    } catch (err) {
      // Check if it's an auth error first
      if (!handleAuthError(err)) {
        console.error('Failed to fetch queue stats:', err);
        setQueueStatsError('Failed to load queue statistics');
        onGpuModalOpen(); // Still open modal to show error
      }
    } finally {
      setQueueStatsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const calculateSuccessRate = () => {
    if (!jobStats || !jobStats.success_rate) return '0.0%';
    return `${jobStats.success_rate.toFixed(1)}%`;
  };

  const formatTotalTime = (seconds) => {
    if (seconds == null || isNaN(seconds)) return 'N/A'; // handle null/undefined/NaN
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatTime = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 60);
    const minutes = seconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${Math.round(minutes)}m`;
  };

  // Add this new helper function to format file sizes
  const formatFileSize = (mb) => {
    if (mb == null || isNaN(mb)) return 'N/A';
    if (mb === 0) return '0 MB';
    
    // Value is already in MB, just format it nicely
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(1)} GB`;
    }
    return `${mb.toFixed(1)} MB`;
  };

  const handleRefresh = async () => {
    try {
      // Refresh job stats, user jobs, and subscription details
      await Promise.all([
        fetchJobStats(),
        fetchUserJobs(),
        fetchSubscriptionDetails(),
        fetchJobEligibility()
      ]);
    } catch (err) {
      // Check if it's an auth error first
      if (!handleAuthError(err)) {
        console.error('Refresh failed:', err);
      }
    }
  };

  // Add the fetchSubscriptionDetails function
  const fetchSubscriptionDetails = async () => {
    try {
      setSubscriptionLoading(true);
      setSubscriptionError(null);
      const details = await apiService.getSubscriptionDetails();
      setSubscriptionDetails(details);
    } catch (err) {
      // Check if it's an auth error first
      if (!handleAuthError(err)) {
        console.error('Failed to fetch subscription details:', err);
        setSubscriptionError('Failed to load subscription details');
      }
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Add the fetchPurchaseSummary function
  const fetchPurchaseSummary = async () => {
    try {
      setPurchaseSummaryLoading(true);
      setPurchaseSummaryError(null);
      const summary = await apiService.getPurchaseSummary();
      setPurchaseSummary(summary);
    } catch (err) {
      console.error('Failed to fetch purchase summary:', err);
      setPurchaseSummaryError('Failed to load purchase summary');
    } finally {
      setPurchaseSummaryLoading(false);
    }
  };

  // Helper functions for subscription display
  const getSubscriptionStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'green';
      case 'inactive':
      case 'cancelled':
        return 'red';
      case 'trial':
        return 'blue';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'premium':
      case 'pro':
        return FaCrown;
      case 'basic':
      case 'standard':
        return FaCreditCard;
      default:
        return FaInfoCircle;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Add this helper function inside the Dashboard component
  const trimJobId = (jobId) => {
    if (jobId.length <= 12) return jobId;
    return `${jobId.substring(0, 8)}...${jobId.slice(-4)}`;
  };

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200">
        <Container maxW="7xl" py={4}>
          <HStack justify="space-between">
            <HStack 
              spacing={1} 
              cursor="pointer" 
              onClick={() => navigate('/')}
              _hover={{ opacity: 0.8 }} 
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: "scale(0.98)" }}
            >
              <Image
                src="/simple-logo.png"
                alt="SRS Logo"
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <NavDropdowns />
              <Button
                leftIcon={<FaCloud />}
                colorScheme="brand"
                variant="ghost"
                size="sm"
              >
                Dashboard
              </Button>
              <Button
                leftIcon={<FaSignOutAlt />}
                variant="ghost"
                size="sm"
                onClick={handleLogout}
              >
                Sign Out
              </Button>
            </HStack>
          </HStack>
        </Container>
      </Box>

      <Container maxW="7xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Hero Section */}
          <Box textAlign="center" py={12}>
            {/* Alert Messages - Either validation required OR low priority queue notice */}
            {!purchaseSummaryLoading && purchaseSummary && (
              <>
                {/* Case 1: No purchases - Account validation required */}
                {purchaseSummary.total_purchases === 0 && (
                  <Alert 
                    status="warning" 
                    borderRadius="xl" 
                    mb={8}
                    maxW="4xl"
                    mx="auto"
                    bg="orange.50"
                    borderWidth="2px"
                    borderColor="orange.300"
                    py={5}
                    px={6}
                  >
                    <AlertIcon color="orange.500" boxSize={6} />
                    <Box flex="1">
                      <Text fontWeight="bold" fontSize="lg" mb={2} color="orange.800">
                        Account Validation Required
                      </Text>
                      <Text fontSize="md" color="orange.900">
                        Verify your account to start running simulations and unlock 2 hours of credits. A one-time charge of €1 will be applied to activate your account. This small verification fee helps prevent abuse and ensures the platform remains available for all users.{' '}
                        <Button
                          variant="link"
                          colorScheme="orange"
                          fontSize="md"
                          fontWeight="bold"
                          p={0}
                          h="auto"
                          minW="auto"
                          onClick={() => navigate('/buy-credits')}
                          textDecoration="underline"
                        >
                          Activate €1 Starter →
                        </Button>
                      </Text>
                    </Box>
                  </Alert>
                )}

                {/* Case 2: 1 purchase + Free Tier - Low priority queue notice */}
                {purchaseSummary.total_purchases === 1 && 
                 purchaseSummary.product_name === 'Free Tier' && (
                  <Alert 
                    status="info" 
                    borderRadius="xl" 
                    mb={8}
                    maxW="4xl"
                    mx="auto"
                    bg="blue.50"
                    borderWidth="2px"
                    borderColor="blue.300"
                    py={5}
                    px={6}
                  >
                    <AlertIcon color="blue.500" boxSize={6} />
                    <Box flex="1">
                      <Text fontWeight="bold" fontSize="lg" mb={2} color="blue.800">
                        You're in the Low Priority Queue
                      </Text>
                      <Text fontSize="md" color="blue.900">
                        Your €1 Starter plan is active. Simulations may experience extended wait times due to low-priority scheduling. Upgrade to a paid plan for <strong>priority queue</strong> and faster processing.{' '}
                        <Button
                          variant="link"
                          colorScheme="blue"
                          fontSize="md"
                          fontWeight="bold"
                          p={0}
                          h="auto"
                          minW="auto"
                          onClick={() => navigate('/buy-credits')}
                          textDecoration="underline"
                        >
                          Buy More Credits →
                        </Button>
                      </Text>
                    </Box>
                  </Alert>
                )}
              </>
            )}

            <Heading size="2xl" color="brand.500" mb={4}>
              Accelerate Your Research with GPU Power
            </Heading>
            <Text fontSize="xl" color="gray.600" maxW="2xl" mx="auto" mb={6}>
              Run complex simulations in minutes, not hours. Our platform provides access to the latest GPU technology for scientific computing and research.
            </Text>
            
            {/* Purchase Summary in Hero */}
            {subscriptionLoading || purchaseSummaryLoading ? (
              <HStack justify="center" spacing={3} mb={8}>
                <Spinner size="sm" />
                <Text fontSize="sm" color="gray.600">Loading account...</Text>
              </HStack>
            ) : subscriptionError || purchaseSummaryError ? (
              <HStack justify="center" spacing={2} mb={8}>
                <Text fontSize="sm" color="red.500">{subscriptionError || purchaseSummaryError}</Text>
                <Button 
                  size="xs" 
                  variant="link" 
                  colorScheme="red" 
                  onClick={() => {
                    if (subscriptionError) fetchSubscriptionDetails();
                    if (purchaseSummaryError) fetchPurchaseSummary();
                  }}
                >
                  Retry
                </Button>
              </HStack>
            ) : subscriptionDetails ? (
              <Box mb={8}>
                <Wrap spacing={6} align="center" justify="center">
                  
                  {/* Replace Current Period with Purchase Summary */}
                  {purchaseSummary && (
                    <>
                      {purchaseSummary.product_name && (
                        <WrapItem>
                          <HStack spacing={2}>
                            <Text fontSize="sm" color="gray.600">Latest Product:</Text>
                            <Badge 
                              variant="subtle" 
                              colorScheme={purchaseSummary.product_name === 'On Demand Usage' ? 'blue' : purchaseSummary.product_name === 'Free Tier' ? 'yellow' : 'gray'}
                              textTransform="capitalize"
                            >
                              {getProductDisplayName(purchaseSummary.product_name)}
                            </Badge>
                          </HStack>
                        </WrapItem>
                      )}
                      
                      {purchaseSummary.status && (
                        <WrapItem>
                          <HStack spacing={2}>
                            <Text fontSize="sm" color="gray.600">Purchase Status:</Text>
                            <Badge 
                              variant="subtle" 
                              colorScheme={purchaseSummary.status === 'succeeded' ? 'green' : purchaseSummary.status === 'pending' ? 'yellow' : 'gray'}
                              textTransform="capitalize"
                            >
                              {purchaseSummary.status}
                            </Badge>
                          </HStack>
                        </WrapItem>
                      )}
                      
                      <WrapItem>
                        <HStack spacing={2}>
                          <Text fontSize="sm" color="gray.600">Total Purchases:</Text>
                          <Text fontSize="sm" fontWeight="medium">
                            {purchaseSummary.total_purchases}
                          </Text>
                        </HStack>
                      </WrapItem>                        
                      
                      <WrapItem>
                        <HStack spacing={2}>
                          <Text fontSize="sm" color="gray.600">Total Spent:</Text>
                          <Text fontSize="sm" fontWeight="medium">
                            €{(purchaseSummary.total_amount / 100).toFixed(0)}
                          </Text>
                        </HStack>
                      </WrapItem>
                      
                      <WrapItem>
                        <HStack spacing={2}>
                          <Text fontSize="sm" color="gray.600">Last Purchase:</Text>
                          <Text fontSize="sm" fontWeight="medium">
                            {purchaseSummary.last_purchase 
                              ? formatDate(purchaseSummary.last_purchase)
                              : 'Never'
                            }
                          </Text>
                        </HStack>
                      </WrapItem>
                    </>
                  )}
                  
                  <WrapItem>
                    <HStack spacing={2}>
                      <Text fontSize="sm" color="gray.600">Member Since:</Text>
                      <Text fontSize="sm" fontWeight="medium">
                        {formatDate(subscriptionDetails.created_at)}
                      </Text>
                    </HStack>
                  </WrapItem>
                </Wrap>
              </Box>
            ) : null}

            <HStack spacing={4} justify="space-between" mt={8}>
              <HStack spacing={4}>
              <Tooltip
                  label={"Read the tutorial to get started"}
                  hasArrow                  
                  placement="top"
                >

                </Tooltip>
                <Tooltip
                  label={
                    !jobEligibility || !jobEligibility.is_eligible 
                      ? jobEligibility?.reason || "Quota limit reached"
                      : `${jobEligibility.remaining_hours} hours remaining`
                  }
                  hasArrow
                  isDisabled={!(!jobEligibility || !jobEligibility.is_eligible)}
                  placement="top"
                >
                  <Button
                    leftIcon={<FaPlay />}
                    colorScheme="brand"
                    size="lg"
                    onClick={() => navigate('/start-simulation')}
                    isDisabled={!jobEligibility || !jobEligibility.is_eligible}
                    isLoading={jobEligibilityLoading}
                    loadingText="Checking eligibility..."
                  >
                    Start Simulation
                  </Button>
                </Tooltip>
                <Button
                  leftIcon={<FaListOl />}
                  colorScheme="brand"
                  variant="outline"
                  size="lg"
                  onClick={handleViewGpuStatus}
                  isLoading={queueStatsLoading}
                >
                  Jobs State
                </Button>

              </HStack>
              <HStack spacing={2}>

              {/* Copilot temporarily hidden — functionality still in development
              <Tooltip label="Try the SRS Copilot (Beta)" hasArrow>
              <Button 
                  colorScheme="pink"
                  variant="outline"
                  size="md"
                  minW="auto"
                  px={3}  
                  onClick={() => navigate('/copilot')}
                  position="relative"
                >
                  <Icon as={FaBrain} />
                  <Badge 
                    colorScheme="orange" 
                    variant="solid" 
                    fontSize="6px" 
                    position="absolute" 
                    top="-1" 
                    right="-1" 
                    borderRadius="full"
                    px={1}
                    lineHeight="1.4"
                  >
                    β
                  </Badge>
              </Button>
              </Tooltip>
              */}

              <Tooltip label="Launch Templates" hasArrow>
              <Button 
                  colorScheme="brand"
                  variant="outline"
                  size="md"
                  minW="auto"
                  px={3}  
                  onClick={() => navigate('/examples')}
                >
                  <Icon as={FaRocket} />
              </Button>
              </Tooltip>

              <Tooltip label="Read the tutorial to get started" hasArrow>
              <Button
                  colorScheme="brand"
                  variant="outline"
                  size="md"
                  minW="auto"
                  px={3}  
                  onClick={() => navigate('/tutorial')}
                  >
                    <Icon as={FaBookOpen } /> 
              </Button>
              </Tooltip>

              <Tooltip label="Buy more credits to run more simulations" hasArrow>
              <Button 
                  colorScheme="brand"
                  variant="outline"
                  size="md"
                  minW="auto"
                  px={3}  
                  onClick={() => navigate('/buy-credits')}
                >
                  <Icon as={FaShoppingCart} />
              </Button>
              </Tooltip>

              <Tooltip label="Manage your account and settings" hasArrow>
              <Button
                size="md"
                onClick={() => navigate('/settings')}
                colorScheme="brand"
                variant="outline"
                minW="auto"
                px={3}
                borderRadius="full"
                aria-label="Settings"
              >
                <Icon as={FaCog} boxSize={5} />
              </Button>
              </Tooltip>

              <Tooltip label="Refresh the page" hasArrow>
              <Button
                  size="md"
                  onClick={handleRefresh}
                  colorScheme="brand"
                  variant="outline"
                  minW="auto"
                  px={3}
                  borderRadius="full"
                  aria-label="Refresh"
                >
                  <FaSyncAlt />
                </Button>
                </Tooltip>
                </HStack>
            </HStack>
          </Box>

          {/* Statistics Grid - Single Row */}
          <SimpleGrid columns={{ base: 2, md: 3, lg: 7 }} spacing={4}>
            <Box bg="white" p={4} rounded="lg" shadow="sm" textAlign="center">
              <Icon as={FaServer} color="brand.500" boxSize={5} mb={2} />
              <Heading size="md" color="brand.500">
                {jobStats ? jobStats.total_jobs : 0}
              </Heading>
              <Text color="gray.600" fontSize="sm">Total Jobs</Text>
            </Box>
            <Box bg="white" p={4} rounded="lg" shadow="sm" textAlign="center">
              <Icon as={FaCheckCircle} color="green.500" boxSize={5} mb={2} />
              <Heading size="md" color="green.500">
                {jobStats ? jobStats.successful_jobs : 0}
              </Heading>
              <Text color="gray.600" fontSize="sm">Successful</Text>
            </Box>
            <Box bg="white" p={4} rounded="lg" shadow="sm" textAlign="center">
              <Icon as={FaTimes} color="red.500" boxSize={5} mb={2} />
              <Heading size="md" color="red.500">
                {jobStats ? jobStats.failed_jobs : 0}
              </Heading>
              <Text color="gray.600" fontSize="sm">Failed</Text>
            </Box>
            <Box bg="white" p={4} rounded="lg" shadow="sm" textAlign="center">
              <Icon as={FaChartLine} color="purple.500" boxSize={5} mb={2} />
              <Heading size="md" color="purple.500">
                {calculateSuccessRate()}
              </Heading>
              <Text color="gray.600" fontSize="sm">Success Rate</Text>
            </Box>
            <Box bg="white" p={4} rounded="lg" shadow="sm" textAlign="center">
              <Icon as={FaHourglassHalf} color="blue.500" boxSize={5} mb={2} />
              <Heading size="md" color="blue.500">
                {jobStats ? formatTotalTime(jobStats.total_processing_time) : 'N/A'}
              </Heading>
              <Text color="gray.600" fontSize="sm">Total Processing Time</Text>
            </Box>
            <Box bg="white" p={4} rounded="lg" shadow="sm" textAlign="center">
              <Icon as={FaClock} color="orange.500" boxSize={5} mb={2} />
              <Heading size="md" color="orange.500">
                {jobStats ? formatTotalTime(jobStats.total_time) : 'N/A'}
              </Heading>
              <Text color="gray.600" fontSize="sm">Total Time</Text>
            </Box>
            <Box bg="white" p={4} rounded="lg" shadow="sm" textAlign="center">
              <Icon as={FaDownload} color="teal.500" boxSize={5} mb={2} />
              <Heading size="md" color="teal.500">
                {jobStats ? formatFileSize(jobStats.total_files_size) : 'N/A'}
              </Heading>
              <Text color="gray.600" fontSize="sm">Total File Size</Text>
            </Box>
          </SimpleGrid>

          {/* Recent Jobs */}
          <Box>
            <HStack justifyContent="space-between" alignItems="center" mb={6}>
              <Heading size="lg">Recent Jobs</Heading>
              <Link 
                color="brand.500" 
                fontWeight="medium"
                _hover={{ textDecoration: "underline" }}
                onClick={() => navigate('/jobs')}
                cursor="pointer"
              >
                View all jobs
              </Link>
            </HStack>
            {jobsLoading ? (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                {[1, 2, 3, 4].map((i) => (
                  <Box key={i} bg="white" p={6} rounded="lg" shadow="sm" textAlign="center">
                    <Spinner size="lg" color="brand.500" mb={3} />
                    <Text color="gray.600">Loading...</Text>
                  </Box>
                ))}
              </SimpleGrid>
            ) : jobsError ? (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {jobsError}
                <Button ml={4} size="sm" onClick={fetchUserJobs}>
                  Retry
                </Button>
              </Alert>
            ) : userJobs.length === 0 ? (
              <Box bg="white" p={8} rounded="lg" shadow="sm" textAlign="center">
                <Icon as={FaServer} color="gray.400" boxSize={12} mb={4} />
                <Heading size="md" color="gray.500" mb={2}>No Jobs Yet</Heading>
                <Text color="gray.600" mb={4}>
                  You haven't started any simulations yet. Start your first simulation to see it here.
                </Text>
                <Tooltip
                  label={
                    !jobEligibility || !jobEligibility.is_eligible 
                      ? jobEligibility?.reason || "Quota limit reached"
                      : `${jobEligibility.remaining_hours} hours remaining`
                  }
                  hasArrow
                  isDisabled={!(!jobEligibility || !jobEligibility.is_eligible)}
                  placement="top"
                >
                  <Button 
                    colorScheme="brand" 
                    leftIcon={<FaPlay />}
                    onClick={() => navigate('/start-simulation')}
                    isDisabled={!jobEligibility || !jobEligibility.is_eligible}
                    isLoading={jobEligibilityLoading}
                    loadingText="Checking eligibility..."                  
                  >
                    Start Your First Simulation
                  </Button>
                </Tooltip>
              </Box>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                {userJobs.map((job) => (
                  <JobCard 
                    key={job.job_id} 
                    job={job} 
                    handleDownload={handleDownload} 
                    handleViewError={handleViewError}
                    downloadingJobs={downloadingJobs}
                  />
                ))}
              </SimpleGrid>
            )}
          </Box>

          {/* Platform Features */}
          <Box bg="white" p={8} rounded="lg" shadow="sm">
            <Heading size="lg" mb={6} textAlign="center">Platform Features</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              <VStack spacing={4} textAlign="center">
                <Icon as={FaMicrochip} color="gray.600" boxSize={12} />
                <Heading size="md">GPU Acceleration</Heading>
                <Text color="gray.600">
                Leverage the latest NVIDIA GPUs for maximum performance
                </Text>
              </VStack>
              <VStack spacing={4} textAlign="center">
                <Icon as={FaCloud} color="brand.500" boxSize={12} />
                <Heading size="md">Cloud-Based</Heading>
                <Text color="gray.600">
                  Access your simulations from anywhere with our cloud platform
                </Text>
              </VStack>
              <VStack spacing={4} textAlign="center">
                <Icon as={FaChartLine} color="green.500" boxSize={12} />
                <Heading size="md">Real-time Monitoring</Heading>
                <Text color="gray.600">
                  Monitor simulation progress and performance in real-time
                </Text>
              </VStack>
            </SimpleGrid>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} mt={8}>
              <VStack spacing={4} textAlign="center">
                <Icon as={FaBolt} color="orange.500" boxSize={12} />
                <Heading size="md">Fast Results</Heading>
                <Text color="gray.600">
                  Get results in minutes, not hours with our optimized platform
                </Text>
              </VStack>
              <VStack spacing={4} textAlign="center">
                <Icon as={FaShieldAlt} color="brand.500" boxSize={12} />
                <Heading size="md">Secure & Reliable</Heading>
                <Text color="gray.600">
                  Enterprise-grade security with 99.9% uptime guarantee
                </Text>
              </VStack>
              <VStack spacing={4} textAlign="center">
                <Icon as={FaBrain} color="purple.500" boxSize={12} />
                <Heading size="md">AI-Powered</Heading>
                <Text color="gray.600">
                  Built with AI at its core to accelerate simulations, enhance accuracy, and optimize performance
                </Text>
              </VStack>
            </SimpleGrid>


          </Box>
        </VStack>
      </Container>
      
      {/* GPU Status Modal */}
      <Modal isOpen={isGpuModalOpen} onClose={onGpuModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FaMicrochip} color="brand.500" boxSize={6} />
              <VStack align="start" spacing={0}>
                <Text fontSize="lg" fontWeight="bold">GPU Queue Status</Text>
                <Text fontSize="sm" color="gray.600">Current processing queue information</Text>
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {queueStatsLoading ? (
              <VStack spacing={4} py={12}>
                <Spinner size="xl" color="brand.500" thickness="4px" />
                <Text color="gray.600">Loading queue statistics...</Text>
              </VStack>
            ) : queueStatsError ? (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <VStack align="start" spacing={0}>
                  <Text fontWeight="medium">Error Loading Queue Stats</Text>
                  <Text fontSize="sm">{queueStatsError}</Text>
                </VStack>
              </Alert>
            ) : queueStats && Object.keys(queueStats).length > 0 ? (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="md" fontWeight="semibold" mb={4} color="gray.700">
                    Jobs Currently in Processing Queue ({Object.keys(queueStats).length})
                  </Text>
                </Box>
                
                <VStack spacing={3} align="stretch">
                  {Object.entries(queueStats).map(([jobId, stats]) => (
                    <Box
                      key={jobId}
                      border="1px"
                      borderColor="gray.200"
                      borderRadius="lg"
                      p={4}
                      bg="gray.50"
                      _hover={{ bg: "gray.100" }}
                      transition="all 0.2s"
                    >
                      <VStack spacing={3} align="stretch">
                        {/* Job Header */}
                        <HStack justify="space-between" align="center">
                          <HStack spacing={3}>
                            <Icon as={FaServer} color="brand.500" boxSize={5} />
                            <VStack align="start" spacing={0}>
                              <Tooltip 
                                label={`Full Job ID: ${jobId}`} 
                                placement="top" 
                                hasArrow
                                bg="gray.800"
                                color="white"
                                fontSize="sm"
                              >
                                <Text 
                                  fontWeight="bold" 
                                  fontSize="md" 
                                  color="gray.800"
                                  cursor="help"
                                >
                                  Job ID: {trimJobId(jobId)}
                                </Text>
                              </Tooltip>
                              <Text fontSize="sm" color="gray.600">
                                Task #{stats.task_id}
                              </Text>
                            </VStack>
                          </HStack>
                          
                          <Badge 
                            colorScheme={stats.ahead_count === 0 ? "green" : stats.ahead_count <= 2 ? "yellow" : "red"}
                            fontSize="sm"
                            px={3}
                            py={1}
                            borderRadius="full"
                          >
                            ⏳ {stats.ahead_count + 1} job{stats.ahead_count + 1 !== 1 ? 's' : ''} ahead
                          </Badge>
                        </HStack>
                        
                        {/* Job Details */}
                        <Divider />
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                          <VStack align="start" spacing={2}>
                            <Text fontSize="sm" fontWeight="medium" color="gray.700">
                              📅 Enqueued Time
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              {new Date(stats.enqueued_at).toLocaleDateString('en-US', {
                                weekday: 'short',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              {new Date(stats.enqueued_at).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })}
                            </Text>
                          </VStack>
                          
                          <VStack align="start" spacing={2}>
                            <VStack align="start" spacing={2}>
  <Text fontSize="sm" fontWeight="medium" color="gray.700">
    📊 Queue Status
  </Text>
  <VStack align="start" spacing={1}>
    <HStack spacing={2}>
      <Box w={2} h={2} bg={stats.ahead_count === 0 ? "green.500" : "yellow.500"} borderRadius="full" />
      <Text fontSize="sm" color={stats.ahead_count === 0 ? "green.600" : "yellow.600"} fontWeight="medium">
        Position: {stats.ahead_count + 2}
      </Text>
    </HStack>
    <Text fontSize="xs" color="gray.500">
      {stats.ahead_count + 1} job{stats.ahead_count + 1 !== 1 ? 's' : ''} ahead of you
    </Text>
  </VStack>
</VStack>
                          </VStack>
                        </SimpleGrid>
                      </VStack>
                    </Box>
                  ))}
                </VStack>
              </VStack>
            ) : (
              <VStack spacing={6} py={12} textAlign="center">
                <Icon as={FaServer} color="gray.300" boxSize={16} />
                <VStack spacing={2}>
                  <Heading size="md" color="gray.500">
                    No Jobs in Queue
                  </Heading>
                  <Text color="gray.600" maxW="sm">
                    You don't have any jobs currently in the processing queue. Start a new simulation to see it here.
                  </Text>
                </VStack>
                <Tooltip
                  label="quota limit reached"
                  hasArrow
                  isDisabled={!(!jobEligibility || !jobEligibility.is_eligible)}
                  placement="top"
                >
                  <Button
                    leftIcon={<FaPlay />}
                    colorScheme="brand"
                    onClick={() => {
                      onGpuModalClose();
                      navigate('/start-simulation');
                    }}
                    isDisabled={!jobEligibility || !jobEligibility.is_eligible}
                  >
                    Start New Simulation
                  </Button>
                </Tooltip>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter borderTop="1px" borderColor="gray.200" bg="gray.50">
            <HStack spacing={3} w="full" justify="space-between">
              <Text fontSize="sm" color="gray.500">
                Queue updates automatically every 30 seconds
              </Text>
              <Button colorScheme="brand" onClick={onGpuModalClose}>
                Close
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Error Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FaExclamationTriangle} color="red.500" />
              <Text>Job Error Details</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontWeight="semibold" color="gray.700" mb={2}>
                  Job Information
                </Text>
                <Box bg="gray.50" p={3} rounded="md">
                  <Text fontSize="sm" color="gray.600">
                    <strong>Job ID:</strong> {selectedError.jobId}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    <strong>Job Name:</strong> {selectedError.jobName}
                  </Text>
                </Box>
              </Box>
              
              <Divider />
              
              <Box>
                <Text fontWeight="semibold" color="red.600" mb={2}>
                  Error Details
                </Text>
                <Box 
                  bg="red.50" 
                  border="1px" 
                  borderColor="red.200" 
                  p={4} 
                  rounded="md"
                  maxH="300px"
                  overflowY="auto"
                >
                  <Code 
                    display="block" 
                    whiteSpace="pre-wrap" 
                    fontSize="sm" 
                    color="red.800"
                    bg="transparent"
                    p={0}
                  >
                    {selectedError.error}
                  </Code>
                </Box>
              </Box>
              
              <Box>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  Please review logs for more details in the Jobs section.
                </Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" variant="outline" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>


      <Footer />
    </Box>
  );
};

export default Dashboard;

